/* eslint-disable indent */
import { useState, useCallback, useRef } from 'react';
import { useStripes } from '@folio/stripes/core';
import { useIntl } from 'react-intl';
import useTenantKy from './useTenantKy';

// Configuration constants based on FOLIO patterns
const DELETION_BATCH_SIZE = 100; // Smaller batches for better UX
const MAX_ENTITIES = 10000; // FOLIO standard maximum
const RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000; // 1 second

const useEnhancedSequentialDeletion = () => {
  const stripes = useStripes();
  const intl = useIntl();
  const ky = useTenantKy({ tenantId: stripes.okapi.tenant });
  const operationRef = useRef(null); // For cancellation control

  const [deletionState, setDeletionState] = useState({
    isDeleting: false,
    currentStep: '',
    progress: {
      completed: 0,
      total: 0,
      percentage: 0,
      currentItem: '',
      currentBatch: 0,
      totalBatches: 0,
      processingRate: 0, // items per second
      estimatedTimeRemaining: 0, // seconds
    },
    error: null,
    completedOperations: [],
    failedOperations: [],
    // Enhanced state for large operations
    isPaused: false,
    isCancelled: false,
    batchMetrics: {
      totalProcessed: 0,
      totalFailed: 0,
      averageProcessingTime: 0,
      currentBatchSize: 0,
    },
  });

  const updateProgress = useCallback((updates) => {
    setDeletionState((prev) => {
      const newProgress = {
        ...prev.progress,
        ...updates.progress,
      };

      // Calculate percentage
      if (newProgress.total > 0) {
        newProgress.percentage = Math.round(
          (newProgress.completed / newProgress.total) * 100,
        );
      }

      // Calculate processing rate and estimated time
      const now = Date.now();
      if (prev.startTime && newProgress.completed > 0) {
        const elapsedSeconds = (now - prev.startTime) / 1000;
        newProgress.processingRate = Math.round(
          newProgress.completed / elapsedSeconds,
        );

        if (newProgress.processingRate > 0) {
          const remaining = newProgress.total - newProgress.completed;
          newProgress.estimatedTimeRemaining = Math.round(
            remaining / newProgress.processingRate,
          );
        }
      }

      return {
        ...prev,
        ...updates,
        progress: newProgress,
        startTime: prev.startTime || now,
      };
    });
  }, []);

  // Helper function to chunk arrays
  const chunkArray = (array, size) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  };

  // Enhanced retry mechanism with exponential backoff
  const retryOperation = async (operation, maxRetries = RETRY_ATTEMPTS) => {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;

        if (attempt === maxRetries) {
          throw error;
        }

        // Exponential backoff
        const delay = RETRY_DELAY * Math.pow(2, attempt - 1);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    throw lastError;
  };

  // Enhanced delete functions with better error handling
  const deleteItem = async (item) => {
    return retryOperation(async () => {
      try {
        await ky.delete(`inventory/items/${item.id}`);
        return item;
      } catch (error) {
        // If item is already deleted (404), consider it success
        if (error.response?.status === 404) {
          return item;
        }
        throw new Error(
          intl.formatMessage(
            {
              id: 'ui-plugin-inventory-templates.safeDelete.error.failedToDeleteItem',
              defaultMessage: 'Failed to delete item {name}: {error}',
            },
            { name: item.barcode || item.id, error: error.message }
          )
        );
      }
    });
  };

  const deleteHolding = async (holding) => {
    return retryOperation(async () => {
      try {
        await ky.delete(`holdings-storage/holdings/${holding.id}`);
        return holding;
      } catch (error) {
        // If holding is already deleted (404), consider it success
        if (error.response?.status === 404) {
          return holding;
        }
        throw new Error(
          intl.formatMessage(
            {
              id: 'ui-plugin-inventory-templates.safeDelete.error.failedToDeleteHolding',
              defaultMessage: 'Failed to delete holding {name}: {error}',
            },
            { name: holding.id, error: error.message }
          )
        );
      }
    });
  };

  const deleteInstance = async (instanceId) => {
    return retryOperation(async () => {
      try {
        await ky.delete(`inventory/instances/${instanceId}`);
        return instanceId;
      } catch (error) {
        // If instance is already deleted (404), consider it success
        if (error.response?.status === 404) {
          return instanceId;
        }
        throw new Error(
          intl.formatMessage(
            {
              id: 'ui-plugin-inventory-templates.safeDelete.error.failedToDeleteInstance',
              defaultMessage: 'Failed to delete instance {name}: {error}',
            },
            { name: instanceId, error: error.message }
          )
        );
      }
    });
  };

  // Batch deletion with progress tracking
  const deleteItemsBatch = async (itemsBatch, batchIndex, totalBatches) => {
    const batchResults = [];
    const batchErrors = [];

    updateProgress({
      currentStep: intl.formatMessage(
        {
          id: 'ui-plugin-inventory-templates.safeDelete.progress.deletingItemsBatch',
          defaultMessage: 'Deleting items batch {currentBatch}/{totalBatches}',
        },
        { currentBatch: batchIndex + 1, totalBatches }
      ),
      progress: {
        currentBatch: batchIndex + 1,
        totalBatches,
        currentItem: intl.formatMessage(
          {
            id: 'ui-plugin-inventory-templates.safeDelete.progress.processingItemsBatch',
            defaultMessage: 'Processing batch of {count} items',
          },
          { count: itemsBatch.length }
        ),
      },
    });

    for (let i = 0; i < itemsBatch.length; i++) {
      // Check for cancellation
      if (operationRef.current?.cancelled) {
        throw new Error(
          intl.formatMessage({
            id: 'ui-plugin-inventory-templates.safeDelete.error.operationCancelled',
            defaultMessage: 'Operation was cancelled by user',
          })
        );
      }

      // Check for pause
      while (operationRef.current?.paused) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      const item = itemsBatch[i];

      try {
        updateProgress({
          progress: {
            currentItem: intl.formatMessage(
              {
                id: 'ui-plugin-inventory-templates.safeDelete.progress.deletingItem',
                defaultMessage: 'Deleting item: {name} ({current}/{total} in batch)',
              },
              {
                name: item.barcode || item.id,
                current: i + 1,
                total: itemsBatch.length
              }
            ),
          },
        });

        const result = await deleteItem(item);
        batchResults.push({
          type: 'item',
          id: item.id,
          name: item.barcode || item.id,
          result,
        });
      } catch (error) {
        batchErrors.push({
          type: 'item',
          id: item.id,
          name: item.barcode || item.id,
          error: error.message,
        });
      }
    }

    return { results: batchResults, errors: batchErrors };
  };

  // Enhanced sequential deletion with batch processing
  const performSequentialDeletion = useCallback(
    async ({
      instanceId,
      holdings = [],
      items = [],
      deletionType = 'complete',
      batchSize = DELETION_BATCH_SIZE,
    }) => {
      try {
        // Initialize operation control
        operationRef.current = {
          cancelled: false,
          paused: false,
        };

        const flatItems = Object.values(items).flat();
        let totalOperations = 1; // Instance deletion


        if (deletionType === 'complete') {
          totalOperations += flatItems.length + holdings.length;
        }

        // Validate maximum entities limit
        if (totalOperations > MAX_ENTITIES) {
          throw new Error(
            intl.formatMessage(
              {
                id: 'ui-plugin-inventory-templates.safeDelete.error.exceedsMaxLimit',
                defaultMessage: 'Operation exceeds maximum limit of {maxEntities} entities. Current total: {currentTotal}',
              },
              { maxEntities: MAX_ENTITIES, currentTotal: totalOperations }
            )
          );
        }

        // Calculate batch information
        const itemBatches = chunkArray(flatItems, batchSize);
        const holdingBatches = chunkArray(holdings, batchSize);
        const totalBatches = itemBatches.length + holdingBatches.length;

        // Initialize state
        const preparingMessage = intl.formatMessage({
          id: 'ui-plugin-inventory-templates.safeDelete.progress.preparing',
          defaultMessage: 'Preparing deletion...',
        });

        setDeletionState({
          isDeleting: true,
          currentStep: preparingMessage,
          progress: {
            completed: 0,
            total: totalOperations,
            percentage: 0,
            currentItem: preparingMessage,
            currentBatch: 0,
            totalBatches,
          },
          error: null,
          completedOperations: [],
          failedOperations: [],
          isPaused: false,
          isCancelled: false,
          batchMetrics: {
            totalProcessed: 0,
            totalFailed: 0,
            averageProcessingTime: 0,
            currentBatchSize: batchSize,
          },
          startTime: Date.now(),
        });

        let completed = 0;
        const completedOps = [];
        const failedOps = [];

        if (deletionType === 'complete') {
          // Step 1: Delete all items in batches
          if (itemBatches.length > 0) {
            updateProgress({
              currentStep: intl.formatMessage(
                {
                  id: 'ui-plugin-inventory-templates.safeDelete.progress.deletingItemsInBatches',
                  defaultMessage: 'Deleting {itemCount} items in {batchCount} batches',
                },
                { itemCount: flatItems.length, batchCount: itemBatches.length }
              ),
            });

            for (
              let batchIndex = 0;
              batchIndex < itemBatches.length;
              batchIndex++
            ) {
              const batch = itemBatches[batchIndex];
              const batchStartTime = Date.now();

              try {
                const { results, errors } = await deleteItemsBatch(
                  batch,
                  batchIndex,
                  itemBatches.length,
                );

                // Process results
                results.forEach((result) => {
                  completedOps.push(result);
                  completed++;
                });

                // Process errors
                errors.forEach((error) => {
                  failedOps.push(error);
                });

                // Update progress
                updateProgress({
                  progress: { completed },
                  batchMetrics: {
                    totalProcessed: completed,
                    totalFailed: failedOps.length,
                    averageProcessingTime:
                      (Date.now() - batchStartTime) / batch.length,
                  },
                });
              } catch (error) {
                // Handle batch failure
                batch.forEach((item) => {
                  failedOps.push({
                    type: 'item',
                    id: item.id,
                    name: item.barcode || item.id,
                    error: error.message,
                  });
                });
              }
            }
          }

          // Step 2: Delete all holdings in batches
          if (holdingBatches.length > 0) {
            updateProgress({
              currentStep: `Deleting ${holdings.length} holdings in ${holdingBatches.length} batches`,
            });

            for (
              let batchIndex = 0;
              batchIndex < holdingBatches.length;
              batchIndex++
            ) {
              const batch = holdingBatches[batchIndex];

              updateProgress({
                currentStep: `Deleting holdings batch ${batchIndex + 1}/${holdingBatches.length}`,
                progress: {
                  currentBatch: itemBatches.length + batchIndex + 1,
                  currentItem: `Processing batch of ${batch.length} holdings`,
                },
              });

              for (let i = 0; i < batch.length; i++) {
                // Check for cancellation
                if (operationRef.current?.cancelled) {
                  throw new Error(
                    intl.formatMessage({
                      id: 'ui-plugin-inventory-templates.safeDelete.error.operationCancelled',
                      defaultMessage: 'Operation was cancelled by user',
                    })
                  );
                }

                // Check for pause
                while (operationRef.current?.paused) {
                  await new Promise((resolve) => setTimeout(resolve, 100));
                }

                const holding = batch[i];

                try {
                  updateProgress({
                    progress: {
                      currentItem: `Deleting holding: ${holding.id} (${i + 1}/${batch.length} in batch)`,
                    },
                  });

                  await deleteHolding(holding);
                  completed++;
                  completedOps.push({
                    type: 'holding',
                    id: holding.id,
                    name: holding.id,
                  });

                  updateProgress({
                    progress: { completed },
                  });
                } catch (error) {
                  failedOps.push({
                    type: 'holding',
                    id: holding.id,
                    name: holding.id,
                    error: error.message,
                  });
                }
              }
            }
          }
        }

        // Step 3: Delete instance
        updateProgress({
          currentStep: 'Deleting instance...',
          progress: { currentItem: 'Deleting instance record' },
        });

        try {
          await deleteInstance(instanceId);
          completed++;
          completedOps.push({
            type: 'instance',
            id: instanceId,
            name: intl.formatMessage({
            id: 'ui-plugin-inventory-templates.safeDelete.entityType.instance',
            defaultMessage: 'Instance',
          }),
          });
        } catch (error) {
          failedOps.push({
            type: 'instance',
            id: instanceId,
            name: intl.formatMessage({
            id: 'ui-plugin-inventory-templates.safeDelete.entityType.instance',
            defaultMessage: 'Instance',
          }),
            error: error.message,
          });
        }

        // Final state update
        updateProgress({
          currentStep: failedOps.length > 0
            ? intl.formatMessage({
                id: 'ui-plugin-inventory-templates.safeDelete.status.completedWithErrors',
                defaultMessage: 'Deletion completed with errors',
              })
            : intl.formatMessage({
                id: 'ui-plugin-inventory-templates.safeDelete.status.completedSuccessfully',
                defaultMessage: 'Deletion completed successfully',
              }),
          progress: {
            completed,
            currentItem: failedOps.length > 0
              ? intl.formatMessage(
                  {
                    id: 'ui-plugin-inventory-templates.safeDelete.error.completedWithErrors',
                    defaultMessage: 'Completed with {count} errors',
                  },
                  { count: failedOps.length }
                )
              : intl.formatMessage({
                  id: 'ui-plugin-inventory-templates.safeDelete.status.allOperationsCompleted',
                  defaultMessage: 'All operations completed successfully',
                }),
          },
          completedOperations: completedOps,
          failedOperations: failedOps,
          isDeleting: false,
        });

        if (failedOps.length > 0) {
          throw new Error(
            intl.formatMessage(
              {
                id: 'ui-plugin-inventory-templates.safeDelete.error.completedWithErrors',
                defaultMessage: 'Deletion completed with {count} errors',
              },
              { count: failedOps.length }
            )
          );
        }

        return {
          success: true,
          completedOperations: completedOps,
          failedOperations: failedOps,
          stats: {
            totalProcessed: completed,
            totalFailed: failedOps.length,
            batchesProcessed: itemBatches.length + holdingBatches.length,
          },
        };
      } catch (error) {
        updateProgress({
          isDeleting: false,
          error: error.message,
          currentStep: intl.formatMessage({
            id: 'ui-plugin-inventory-templates.safeDelete.status.failed',
            defaultMessage: 'Deletion failed',
          }),
        });

        throw error;
      }
    },
    [ky, updateProgress],
  );

  // Control functions
  const pauseDeletion = useCallback(() => {
    if (operationRef.current) {
      operationRef.current.paused = true;
      updateProgress({
        isPaused: true,
        currentStep: intl.formatMessage({
          id: 'ui-plugin-inventory-templates.safeDelete.status.paused',
          defaultMessage: 'Deletion paused',
        }),
      });
    }
  }, [updateProgress]);

  const resumeDeletion = useCallback(() => {
    if (operationRef.current) {
      operationRef.current.paused = false;
      updateProgress({
        isPaused: false,
        currentStep: intl.formatMessage({
          id: 'ui-plugin-inventory-templates.safeDelete.status.resumed',
          defaultMessage: 'Deletion resumed',
        }),
      });
    }
  }, [updateProgress]);

  const cancelDeletion = useCallback(() => {
    if (operationRef.current) {
      operationRef.current.cancelled = true;
      updateProgress({
        isCancelled: true,
        isDeleting: false,
        currentStep: intl.formatMessage({
          id: 'ui-plugin-inventory-templates.safeDelete.status.cancelled',
          defaultMessage: 'Deletion cancelled',
        }),
        error: intl.formatMessage({
          id: 'ui-plugin-inventory-templates.safeDelete.error.operationCancelled',
          defaultMessage: 'Operation was cancelled by user',
        }),
      });
    }
  }, [updateProgress]);

  const resetDeletionState = useCallback(() => {
    operationRef.current = null;
    setDeletionState({
      isDeleting: false,
      currentStep: '',
      progress: {
        completed: 0,
        total: 0,
        percentage: 0,
        currentItem: '',
        currentBatch: 0,
        totalBatches: 0,
        processingRate: 0,
        estimatedTimeRemaining: 0,
      },
      error: null,
      completedOperations: [],
      failedOperations: [],
      isPaused: false,
      isCancelled: false,
      batchMetrics: {
        totalProcessed: 0,
        totalFailed: 0,
        averageProcessingTime: 0,
        currentBatchSize: 0,
      },
    });
  }, []);

  return {
    deletionState,
    performSequentialDeletion,
    resetDeletionState,
    // Enhanced control functions
    pauseDeletion,
    resumeDeletion,
    cancelDeletion,
    // Configuration options
    DELETION_BATCH_SIZE,
    MAX_ENTITIES,
  };
};

export default useEnhancedSequentialDeletion;
